import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip
} from '@mui/material';
import { FolderOutlined, PersonOutline } from '@mui/icons-material';
import { projectService } from '../services/api';
import Logo from '../components/Logo';

const ROLE_LABELS = {
  admin: 'Admin',
  editor: 'Membre',
  viewer: 'Observateur',
  member: 'Membre'
};

function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();

  // 'loading' | 'ready' | 'accepting' | 'success' | 'expired' | 'wrong_user' | 'error'
  const [status, setStatus] = useState('loading');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    projectService.getInviteInfo(token)
      .then(res => {
        if (!cancelled) {
          setInviteInfo(res.data);
          setStatus('ready');
        }
      })
      .catch(err => {
        if (!cancelled) {
          const code = err.response?.data?.error;
          if (code === 'expired') setStatus('expired');
          else if (code === 'already_used') setStatus('already_used');
          else setStatus('error');
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = async () => {
    setStatus('accepting');
    try {
      await projectService.acceptInvitation(token);
      setStatus('success');
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === 'expired') setStatus('expired');
      else if (code === 'wrong_user') {
        setErrorMsg(`Cette invitation a été envoyée à ${inviteInfo?.email}. Connectez-vous avec ce compte.`);
        setStatus('wrong_user');
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Logo sx={{ mb: 1 }} />

        {status === 'loading' && (
          <>
            <CircularProgress />
            <Typography color="text.secondary">Chargement de l'invitation...</Typography>
          </>
        )}

        {status === 'ready' && inviteInfo && (
          <>
            <Typography variant="h6">Vous avez été invité !</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', my: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonOutline fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Invité par <strong>{inviteInfo.inviterName}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderOutlined fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Projet : <strong>{inviteInfo.projectTitle}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label={ROLE_LABELS[inviteInfo.role] || inviteInfo.role}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>
            <Button variant="contained" fullWidth size="large" onClick={handleAccept}>
              Rejoindre le projet
            </Button>
            <Button variant="text" size="small" onClick={() => navigate('/dashboard')}>
              Refuser
            </Button>
          </>
        )}

        {status === 'accepting' && (
          <>
            <CircularProgress />
            <Typography color="text.secondary">Traitement en cours...</Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Typography variant="h6" color="success.main">Invitation acceptée !</Typography>
            <Typography variant="body2" color="text.secondary">
              Vous êtes maintenant membre du projet <strong>{inviteInfo?.projectTitle}</strong>.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/dashboard')}>
              Accéder au tableau de bord
            </Button>
          </>
        )}

        {status === 'expired' && (
          <>
            <Typography variant="h6" color="error">Lien expiré</Typography>
            <Typography variant="body2" color="text.secondary">
              Ce lien d'invitation a expiré. Demandez un nouvel envoi à l'organisateur du projet.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>Retour</Button>
          </>
        )}

        {status === 'already_used' && (
          <>
            <Typography variant="h6" color="warning.main">Invitation déjà utilisée</Typography>
            <Typography variant="body2" color="text.secondary">
              Ce lien a déjà été accepté.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/dashboard')}>
              Accéder au tableau de bord
            </Button>
          </>
        )}

        {status === 'wrong_user' && (
          <>
            <Typography variant="h6" color="error">Mauvais compte</Typography>
            <Typography variant="body2" color="text.secondary">{errorMsg}</Typography>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>Retour</Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Typography variant="h6" color="error">Invitation introuvable</Typography>
            <Typography variant="body2" color="text.secondary">
              Ce lien est invalide ou a déjà été utilisé.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>Retour</Button>
          </>
        )}
      </Paper>
    </Container>
  );
}

export default AcceptInvite;
